import * as styles from "./ClientEditorBody.css";

import { useAtom, useAtomValue } from "jotai";
import type { JSX } from "react";

import { Spinner } from "@frontend/components/atoms/Spinner";
import {
  Tabs,
  TabsContent,
  TabsIndicator,
  TabsList,
  TabsPanel,
  TabsTab,
} from "@frontend/components/atoms/Tabs";
import { Trans, useLingui } from "@lingui/react/macro";

import { clientEditorAtom, editorTabAtom } from "../stores/editor";
import type { ClientEditorTab } from "../stores/editor";
import { CommercialConditionsTab } from "./CommercialConditionsTab";
import { ContactDetailsTab } from "./ContactDetailsTab";
import { MainInformations } from "./MainInformations";
import { NotesTab } from "./NotesTab";
import { ProjectsTab } from "./ProjectsTab";

export type ClientEditorBodyProps = {
  loading: boolean;
  error: string | null;
  conflict: boolean;
  stickyTabs?: boolean;
};

export function ClientEditorBody({
  loading,
  error,
  conflict,
  stickyTabs = false,
}: ClientEditorBodyProps): JSX.Element {
  const { t } = useLingui();

  const { client } = useAtomValue(clientEditorAtom);

  const [tab, setTab] = useAtom(editorTabAtom);

  if (loading) {
    return (
      <div className={styles.loading}>
        <Spinner aria-label={t`Loading`} />
      </div>
    );
  }

  return (
    <div className={styles.body}>
      {error ? (
        <p className={styles.alert} role="alert">
          {conflict ? (
            <Trans>
              Someone else saved this client while it was open. Reopen it to see
              their version, then make your change again.
            </Trans>
          ) : (
            error
          )}
        </p>
      ) : null}

      <Tabs
        value={tab}
        onValueChange={(value) => setTab(value as ClientEditorTab)}
        variant="underline"
        className={styles.tabs}
      >
        <TabsList sticky={stickyTabs}>
          <TabsTab value="main" icon="Info">
            <Trans>Main information</Trans>
          </TabsTab>

          <TabsTab value="contact" icon="Contact">
            <Trans>Contact details</Trans>
          </TabsTab>

          {null === client ? null : (
            <TabsTab value="projects" icon="FolderKanban">
              <Trans>Projects</Trans>
            </TabsTab>
          )}

          <TabsTab value="notes" icon="MessageSquare">
            <Trans>Notes</Trans>
          </TabsTab>

          {null === client ? null : (
            <TabsTab value="conditions" icon="ReceiptText">
              <Trans>Commercial conditions</Trans>
            </TabsTab>
          )}

          <TabsIndicator />
        </TabsList>

        <TabsContent className={styles.tabsContent}>
          <TabsPanel value="main">
            <MainInformations />
          </TabsPanel>

          <TabsPanel value="contact">
            <ContactDetailsTab />
          </TabsPanel>

          {null === client ? null : (
            <TabsPanel value="projects">
              <ProjectsTab />
            </TabsPanel>
          )}

          <TabsPanel value="notes" className={styles.notesPanel}>
            <NotesTab />
          </TabsPanel>

          {null === client ? null : (
            <TabsPanel value="conditions">
              <CommercialConditionsTab clientId={client.id} />
            </TabsPanel>
          )}
        </TabsContent>
      </Tabs>
    </div>
  );
}
